import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { cn } from "@/core/utils/utils";
import { useGetUsersWithStats } from "@/user/query";
import { DetailedUserWithStats } from "@/user/user.d";
import { isAdmin, isTech } from "@/user/utils";

const RANK_TO_EMOJI = {
  1: "🥇",
  2: "🥈",
  3: "🥉",
};

interface SignupLeaderboardItemProps {
  user: DetailedUserWithStats;
  rank: number;
  isLast: boolean;
  businessId: string;
}

const SignupLeaderboardItem = ({
  user,
  rank,
  isLast,
  businessId,
}: SignupLeaderboardItemProps) => {
  const navigate = useNavigate();
  const { updateSidebar } = useBusinessSettingsSidebar();

  return (
    <Stack
      gap="sm"
      direction="row"
      align="center"
      className={cn("h-12", !isLast && "border-b border-b-border")}
      fullWidth
      role="button"
      onClick={() => {
        navigate(
          `/business-settings/${businessId}/users/${isTech(user.role) ? "technicians" : "web-app-users"}`,
        );
        updateSidebar({
          section: isTech(user.role) ? "technicians" : "users",
          mode: SIDEBAR_MODES.view,
          selectedItemId: user.id,
          isOpen: true,
        });
      }}
    >
      {[1, 2, 3].includes(rank) ? (
        <Typography variant="headlineW3" className="w-[31px] text-center">
          {RANK_TO_EMOJI[rank as keyof typeof RANK_TO_EMOJI]}
        </Typography>
      ) : (
        <Typography variant="subtitleW2" className="w-[31px] text-center">
          {rank}
        </Typography>
      )}
      <Typography variant="bodyW1" className="line-clamp-2 flex-1">
        {user.first_name} {user.last_name}
      </Typography>
      <Typography variant="bodyW1">{user.stats?.signup_count}</Typography>
    </Stack>
  );
};

interface SignupLeaderboardProps {
  className?: string;
  businessId: string;
}

export const SignupLeaderboard = ({
  className,
  businessId,
}: SignupLeaderboardProps) => {
  const { data: users = [] } = useGetUsersWithStats(
    businessId,
    "last_thirty_days",
  );

  const usersWithoutAdmins = useMemo(
    () => users.filter((user) => !isAdmin(user.role)),
    [users],
  );

  const sortedUsers = useMemo(
    () =>
      usersWithoutAdmins.sort(
        (a, b) => (b.stats?.signup_count ?? 0) - (a.stats?.signup_count ?? 0),
      ),
    [usersWithoutAdmins],
  );

  return (
    <Stack fullWidth className={className}>
      <Typography variant="subtitleW3" className="w-full pr-4 text-center">
        Sign-ups last 30 days
      </Typography>
      <ScrollArea className="w-full flex-1 pr-4">
        <Stack gap="none" fullWidth>
          {sortedUsers.map((user, index) => (
            <SignupLeaderboardItem
              key={user.id}
              user={user}
              businessId={businessId}
              rank={
                sortedUsers.findIndex(
                  (t) => t.stats?.signup_count === user.stats?.signup_count,
                ) + 1
              }
              isLast={index === sortedUsers.length - 1}
            />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
